import { TriangleAlert } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";

import {
  AnyPermissionsKey,
  PermissionsKeys,
} from "components/permissions/permission-keys";
import { ChangeSummary } from "./change-summary";

interface ConfirmChangesCardProps {
  changes: ChangeSummary;
  canSubmit: boolean;
  isLoading: boolean;
  onSubmit: () => void;
  canCancel: boolean;
  onCancel: () => void;
  newPlanIsPermitted: (keys: AnyPermissionsKey[]) => boolean;
}

export const ConfirmChangesCard = ({
  changes,
  canSubmit,
  isLoading,
  onSubmit,
  canCancel,
  onCancel,
  newPlanIsPermitted,
}: ConfirmChangesCardProps) => {
  const showDetails =
    changes.changeType !== "no_selection" && changes.changeType !== "same";

  return (
    <Card className="border-primary/20 shadow-sm">
      <CardHeader>
        <CardTitle>Order Summary</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert variant="info">
          <AlertDescription>{changes.infoText}</AlertDescription>
        </Alert>

        {showDetails && (
          <>
            {changes.changeType === "downgrade" &&
              !newPlanIsPermitted([PermissionsKeys.membersUpdate]) && (
                <Alert variant="warning">
                  <TriangleAlert />
                  <AlertDescription>
                    <strong>WARNING,</strong> All members who are not the{" "}
                    <strong>owner</strong> will have their access to Hub
                    disabled when your subscription downgrades.
                  </AlertDescription>
                </Alert>
              )}

            <div className="flex flex-col gap-1.5">
              <p className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
                Subscription
              </p>
              {changes.subscription.map((item, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{item.lhs}</span>
                  <span className="font-medium">{item.rhs}</span>
                </div>
              ))}
            </div>

            <Separator />

            <div className="flex flex-col gap-1.5">
              <p className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
                Charges
              </p>
              {changes.charges
                .filter((x) => !x.hidden)
                .map((charge, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{charge.lhs}</span>
                    <span className="font-medium">{charge.rhs}</span>
                  </div>
                ))}
            </div>

            <Separator />

            <div className="flex items-baseline justify-between">
              <p className="text-base font-semibold">Total Due</p>
              <p className="text-xl font-bold">{changes.totalDue}</p>
            </div>

            {changes.trialChargeWarning && (
              <Alert variant="warning">
                <TriangleAlert />
                <AlertDescription>
                  {changes.trialChargeWarning}
                </AlertDescription>
              </Alert>
            )}
          </>
        )}

      </CardContent>
      <CardFooter className="flex justify-end gap-2">
        <Button variant="ghost" disabled={!canCancel} onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={!canSubmit || isLoading} onClick={onSubmit}>
          {isLoading ? (
            <>
              <Spinner />
              Purchase
            </>
          ) : (
            "Purchase"
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};
